import { useState, useEffect, useCallback, useRef } from "react";
import { cn } from "@/lib/utils";
import { useYouTubePlayer } from "@/hooks/useYouTubePlayer";
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
} from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";

export interface VideoItem {
  id: string;
  title: string;
  channel?: string;
  thumbnail?: string;
  duration?: string;
}


interface BasicPlayerProps {
  playlist?: VideoItem[];
  initialVideoId?: string;
  initialIndex?: number;
  autoPlay?: boolean;
  onVideoChange?: (video: VideoItem, index: number) => void;
  onPlaylistEnd?: () => void;
  onClose?: () => void;
  helpOverlayOpen?: boolean;
  className?: string;
}

export const BasicPlayer = ({
  playlist = [],
  initialVideoId,
  initialIndex = 0,
  autoPlay = false,
  onVideoChange,
  onPlaylistEnd,
  onClose,
  helpOverlayOpen = false,
  className,
}: BasicPlayerProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Note: Skin theming is now handled via CSS custom properties scoped to the component

  const progressInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  const currentVideo = playlist[currentIndex] || null;
  const videoId = currentVideo?.id || initialVideoId || "";

  const handlePlayerReady = useCallback(() => {
    setIsLoading(false);
    if (autoPlay && videoId) {
      play();
    }
  }, [autoPlay, videoId]);

  const handleStateChange = useCallback((playing: boolean) => {
    setIsPlaying(playing);
  }, []);

  const handleVideoEnd = useCallback(() => {
    if (currentIndex < playlist.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      onPlaylistEnd?.();
    }
  }, [currentIndex, playlist.length, onPlaylistEnd]);

  const {
    containerRef,
    play,
    pause,
    getCurrentTime,
    getDuration,
    seekTo,
    setVolume: setPlayerVolume,
    mute,
    unmute,
    getIsMuted,
    isPlayerReady,
  } = useYouTubePlayer({
    videoId,
    autoPlay,
    onReady: handlePlayerReady,
    onStateChange: handleStateChange,
    onEnd: handleVideoEnd,
  });

  // Notify on video change
  useEffect(() => {
    if (currentVideo) {
      onVideoChange?.(currentVideo, currentIndex);
    }
  }, [currentVideo, currentIndex, onVideoChange]);

  // Progress tracking
  useEffect(() => {
    if (isPlaying) {
      progressInterval.current = setInterval(() => {
        const time = getCurrentTime();
        const dur = getDuration();
        if (time !== undefined) setCurrentTime(time);
        if (dur !== undefined && dur > 0) setDuration(dur);
      }, 500);
    }
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [isPlaying, getCurrentTime, getDuration]);

  // Volume sync
  useEffect(() => {
    if (isPlayerReady) {
      setPlayerVolume(isMuted ? 0 : volume);
    }
  }, [volume, isMuted, isPlayerReady, setPlayerVolume]);

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle shortcuts when not typing in an input
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (event.key.toLowerCase()) {
        case 'f':
          event.preventDefault();
          toggleFullscreen();
          break;
        case ' ':
          event.preventDefault();
          handlePlayPause();
          break;
        case 'arrowleft':
          event.preventDefault();
          handleSeekBackward();
          break;
        case 'arrowright':
          event.preventDefault();
          handleSeekForward();
          break;
        case 'arrowup':
          event.preventDefault();
          handleVolumeUp();
          break;
        case 'arrowdown':
          event.preventDefault();
          handleVolumeDown();
          break;
        case 'm':
          event.preventDefault();
          toggleMute();
          break;
        case 'escape':
          if (isFullscreen) {
            event.preventDefault();
            document.exitFullscreen().catch(() => setIsFullscreen(false));
          } else if (onClose && !helpOverlayOpen) {
            event.preventDefault();
            onClose();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPlaying, currentIndex, playlist.length, isFullscreen, helpOverlayOpen]);

  const handlePlayPause = () => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < playlist.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleSeek = (value: number[]) => {
    const time = value[0];
    seekTo(time);
    setCurrentTime(time);
  };

  const handleVolumeChange = (value: number[]) => {
    setVolume(value[0]);
    if (value[0] > 0 && isMuted) {
      setIsMuted(false);
    }
  };

  const handleVolumeUp = () => {
    setVolume((prev) => Math.min(100, prev + 5));
  };

  const handleVolumeDown = () => {
    setVolume((prev) => Math.max(0, prev - 5));
  };

  const handleSeekForward = () => {
    const currentTime = getCurrentTime();
    seekTo(currentTime + 10);
  };

  const handleSeekBackward = () => {
    const currentTime = getCurrentTime();
    seekTo(Math.max(0, currentTime - 10));
  };

  const toggleMute = () => {
    const currentlyMuted = getIsMuted();
    if (currentlyMuted) {
      unmute();
    } else {
      mute();
    }
    // Update local state to reflect the change
    setIsMuted(!currentlyMuted);
  };

  const toggleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        // Enter fullscreen
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        // Exit fullscreen
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
      // Fallback to UI-only fullscreen if browser fullscreen fails
      setIsFullscreen((prev) => !prev);
    }
  };


  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
      <div
        className={cn(
        "flex flex-col w-full bg-background relative",
          isFullscreen && "fixed inset-0 z-50 bg-black",
          className
        )}
      style={isFullscreen ? {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999
      } : undefined}
    >
      {/* Video Container */}
      <div
        className="relative w-full"
        style={{
          aspectRatio: isFullscreen ? 'auto' : '16/9',
          minHeight: isFullscreen ? '100vh' : '400px',
          height: isFullscreen ? '100vh' : 'auto',
          flex: isFullscreen ? '1' : 'unset'
        }}
      >
        {/* YouTube Player Container */}
        <div className="absolute inset-0 w-full h-full">
          <div
            ref={containerRef}
            className="w-full h-full"
            style={{
              minWidth: '320px',
              minHeight: '180px'
            }}
          />
        </div>

        {/* Loading State */}
        {isLoading && videoId && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <div className="text-white text-sm">Loading video...</div>
          </div>
        )}

        {/* No Video State */}
        {!videoId && (
          <div className="absolute inset-0 flex items-center justify-center text-white bg-black/50">
            No video selected
          </div>
        )}
      </div>

      {/* Bottom Controls */}
      <div className="p-3 space-y-2 bg-white/90 dark:bg-gray-900/95 backdrop-blur-sm rounded-b-lg border-t border-gray-200 dark:border-gray-700">
        {/* Seek Bar */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="w-10 text-right">{formatTime(currentTime)}</span>
          <div className="flex-1 relative">
            <Slider
              value={[currentTime]}
              max={duration || 100}
              step={1}
              onValueChange={handleSeek}
              disabled={isLoading}
              className="w-full [&_[role=slider]]:bg-white [&_[role=slider]]:border-2 [&_[role=slider]]:border-gray-400 [&_[role=slider]]:shadow-lg [&_[role=slider]]:h-4 [&_[role=slider]]:w-4 [&_[role=slider]]:hover:scale-110 [&_[role=slider]]:transition-transform [&_[role=slider]]:ring-2 [&_[role=slider]]:ring-blue-500/50 [&_[role=slider]]:ring-offset-1 [&_[role=slider]]:ring-offset-white [&>span:first-child]:bg-gray-300 [&>span:first-child]:h-2 [&>span:first-child]:rounded-full [&>span:first-child>span]:bg-blue-600 [&>span:first-child>span]:rounded-full"
            />
          </div>
          <span className="w-10">{formatTime(duration)}</span>
        </div>

        {/* Transport + Volume + Playlist Toggle */}

        {/* Transport + Volume + Playlist Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePrevious}
              disabled={currentIndex === 0 || playlist.length === 0}
              className="h-8 w-8"
            >
              <SkipBack className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={handlePlayPause}
              disabled={!videoId || isLoading}
              className="h-10 w-10"
            >
              {isPlaying ? (
                <Pause className="h-5 w-5" />
              ) : (
                <Play className="h-5 w-5" />
              )}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={handleNext}
              disabled={
                currentIndex >= playlist.length - 1 || playlist.length === 0
              }
              className="h-8 w-8"
            >
              <SkipForward className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMute}
              className="h-8 w-8"
            >
              {isMuted || volume === 0 ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </Button>
            <div className="w-20 bg-gray-300/50 rounded-full h-1.5 player-accent">
              <Slider
                value={[isMuted ? 0 : volume]}
                max={100}
                step={1}
                onValueChange={handleVolumeChange}
                disabled={isLoading}
                className="w-full [&_[role=slider]]:bg-white [&_[role=slider]]:border-gray-300 [&_[role=slider]]:shadow-md [&_[role=slider]]:hover:scale-110 [&_[role=slider]]:transition-transform [&_[role=slider]:focus]:ring-2 [&_[role=slider]:focus]:ring-purple-500 [&_[role=slider]:focus]:ring-offset-2 [&_[role=slider]:focus]:ring-offset-gray-900"
              />
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={toggleFullscreen}
              className="h-8 w-8"
              title={isFullscreen ? 'Exit Fullscreen (F)' : 'Fullscreen (F)'}
            >
              {isFullscreen ? (
                <Minimize className="h-4 w-4" />
              ) : (
                <Maximize className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
};
